import { Environment, ModalType, ThemeType } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import AppContext, { Language } from '../AppContext';
import LoadingPage from './LoadingPage';
import Login from './Login';
import Navigator from './Navigator';
import ProtectedFrame from './ProtectedFrame';
import PublicFrame from './PublicFrame';
import RedirectToLicenseManagement from './appService/RedirectToLicenseManagement';
import DataManagement from './dataManagement/DataManagement';
import ErrorMsg, { ApplicationError } from './errorHandling/ErrorMsg';
import JoinOrganizationPage from './joinOrganization/JoinOrganizationPage';
import InviteExpired from './joinOrganization/error-pages/InviteExpired';
import WrongEmail from './joinOrganization/error-pages/WrongEmail';
import UserProfile from './member/UserProfile';
import OrganizationManagement from './organizationManagement/OrganizationManagement';
import ProductsManagement from './productsManagement/ProductsManagement';
import SitesManagement from './sitesManagement/SitesManagement';
import SysadministrationContainer from './sysadministration/SysadministrationContainer';
import LicenseManagement from './system-licences/LicenseManagement';
import UserManagement from './userManagement/UserManagement';
import AllUsersContainer from './sysadministration/AllUsersContainer';
import GMGActivityContainer from './activityManagement/GMGActivityContainer';
import OrganizationActivityContainer from './activityManagement/OrganizationActivityContainer';
import SignUp, { userPoolParams } from './SignUp';
import SignedUp from './SignedUp';
import AcceptInvitationPage from './joinOrganization/AcceptInvitation';
import OrganizationRouteBoundary from './OrganizationRouteBoundary';
import { ROUTE_SEGMENTS } from 'src/routes';
import { NotFoundPage } from './NotFoundPage';
import RedirectFromPartnerPortal from './partnerPortal/RedirectFromPartnerPortal';

export interface AppProps {
    modal?: ModalType;
    signOut: () => void;
    theme: ThemeType;
    onChangeTheme: (theme: ThemeType) => void;
    onChangeLanguage: (language: Language) => void;
    errorType: undefined | ApplicationError;
    env: Environment;
    handleUserUpdateAfterRefreshingToken: () => void;
    userPoolParams: userPoolParams;
    apiEndpoint: string;
};


const createRoutes = (props: AppProps, showDataAndAccessManagement: boolean, showSitesPage: boolean) =>

    createBrowserRouter(
        createRoutesFromElements(
            <Route path="/">
                <Route index element={<Navigate to={ROUTE_SEGMENTS.start} />} />

                {/* redirect to external AWS sign in URL and back to app. AWS does not support to redirect to a specific target URL, so we need to redirect back to a fix URL. */}
                <Route path={ROUTE_SEGMENTS.login} element={<Login />} />
                <Route path={ROUTE_SEGMENTS.authenticated} element={<LoadingPage />} />

                {/* redirect to external AWS sign up URL and back to app.*/}
                <Route path={ROUTE_SEGMENTS.signup} element={<SignUp urlParams={props.userPoolParams} />} />
                <Route path={ROUTE_SEGMENTS.signedUp} element={<SignedUp />} />

                <Route element={<ProtectedFrame {...props} />}>

                    <Route path={`${ROUTE_SEGMENTS.acceptedInvitation}/:userId`}>
                        <Route index element={
                            <AcceptInvitationPage
                                onUserUpdateAfterJoiningOrganization={props.handleUserUpdateAfterRefreshingToken}
                            />}
                        />
                        <Route path={ROUTE_SEGMENTS.expired} element={<InviteExpired />} />
                        <Route path={ROUTE_SEGMENTS.wrongEmail} element={<WrongEmail signOut={props.signOut} />} />
                        <Route path={ROUTE_SEGMENTS.userNotInOrg} element={<InviteExpired />} />
                    </Route>

                    <Route path={ROUTE_SEGMENTS.start} element={<Navigator />} />
                    <Route path={ROUTE_SEGMENTS.userWithoutOrg} element={<ErrorMsg errorType='userWithoutOrg' />} />
                    <Route path={ROUTE_SEGMENTS.profile} element={<UserProfile
                        onUserUpdateAfterRefreshingToken={props.handleUserUpdateAfterRefreshingToken} />}
                    />

                    <Route path={ROUTE_SEGMENTS.organizations}>
                        <Route index element={<SysadministrationContainer />} />
                        <Route path={ROUTE_SEGMENTS.allUsers} element={<AllUsersContainer />} />
                        <Route path={ROUTE_SEGMENTS.gmgActivity} element={<GMGActivityContainer />} />

                        <Route path=":orgId" element={<OrganizationRouteBoundary />}>
                            <Route index element={<OrganizationManagement />} />
                            <Route path={ROUTE_SEGMENTS.users} element={<UserManagement
                                onUserUpdateAfterSelfEditing={props.handleUserUpdateAfterRefreshingToken}
                            />} />
                            <Route path={ROUTE_SEGMENTS.systemLicences} element={<LicenseManagement />} />
                            <Route path={ROUTE_SEGMENTS.products} element={<ProductsManagement />} />
                            {showSitesPage && (
                                <Route path={ROUTE_SEGMENTS.sites} element={<SitesManagement />} />
                            )}
                            <Route path={ROUTE_SEGMENTS.activities} element={<OrganizationActivityContainer />} />
                            {showDataAndAccessManagement && (
                                <Route path={ROUTE_SEGMENTS.data} element={<DataManagement />} />
                            )}
                        </Route>
                    </Route>

                    <Route path={ROUTE_SEGMENTS.services}>
                        <Route path={ROUTE_SEGMENTS.appService}>
                            <Route
                                path={ROUTE_SEGMENTS.createTemporaryLicenses}
                                element={<RedirectToLicenseManagement isTemporaryLicensesRequest={true} />}
                            />
                            <Route
                                path={ROUTE_SEGMENTS.systemLicences}
                                element={<RedirectToLicenseManagement isTemporaryLicensesRequest={false} />}
                            />
                        </Route>
                        <Route path={ROUTE_SEGMENTS.partnerPortal}>
                            <Route index element={<RedirectFromPartnerPortal />} />
                        </Route>
                    </Route>
                    <Route path="*" element={<NotFoundPage />} />
                </Route>

                {/* TODO: As soon as we get a second public page, we need a common parent URL for all public pages.  */}
                <Route
                    path={ROUTE_SEGMENTS.join}
                    element={<PublicFrame errorType={props.errorType} theme={props.theme} />}
                >
                    <Route path=":userId">
                        <Route index element={
                            <JoinOrganizationPage publicRequestEndpoint={props.apiEndpoint} />}
                        />
                        <Route path={ROUTE_SEGMENTS.userNotInOrg} element={<InviteExpired />} />
                    </Route>
                </Route>

            </Route>,

        ),
    );

const App: FunctionComponent<AppProps> = (props) => {
    const context = useContext(AppContext);
    return <RouterProvider router={createRoutes(props, context.featureFlags.showDataAndAccessManagement, context.featureFlags.showSitesPage)} />;
};

export default App;
